const ERROR_STATE_BY_STATUS = {
  400: "validation",
  401: "authentication",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  410: "expired",
  413: "too_large",
  422: "semantic_invalid",
  429: "rate_limited",
  503: "server_error",
};

export function unwrapSheetsData(payload) {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return payload;
}

function getSheetId(sheet) {
  return sheet?.sheet_id || sheet?.id || null;
}

export function normalizeSheet(sheet) {
  const source = unwrapSheetsData(sheet);
  const sheet_id = getSheetId(source);
  if (!sheet_id) return null;

  return {
    sheet_id,
    name: source.name || "",
    description: source.description || "",
    type: source.type || source.sheet_type || null,
    state: source.state || source.status || null,
    service_id: source.service_id || null,
    version: source.version ?? null,
    capabilities: source.capabilities || {},
    counts: source.counts || {},
  };
}

export function normalizeSheetList(payload) {
  const data = unwrapSheetsData(payload) || {};
  const items = Array.isArray(data) ? data : data.sheets || data.items || [];
  const sheets_by_id = {};
  const sheet_ids = [];

  items.forEach((item) => {
    const sheet = normalizeSheet(item);
    if (!sheet || sheets_by_id[sheet.sheet_id]) return;
    sheets_by_id[sheet.sheet_id] = sheet;
    sheet_ids.push(sheet.sheet_id);
  });

  return { sheet_ids, sheets_by_id };
}

function normalizeFieldValues(fields) {
  const field_values = {};
  const values = {};

  if (Array.isArray(fields)) {
    fields.forEach((field) => {
      const field_id = field?.field_id || field?.crm_schema_column_id;
      if (field_id == null) return;
      field_values[field_id] = field.value;
      if (field.slug) values[field.slug] = field.value;
    });
    return { field_values, values };
  }

  if (fields && typeof fields === "object") {
    Object.entries(fields).forEach(([field_id, field]) => {
      if (field && typeof field === "object" && "value" in field) {
        field_values[field_id] = field.value;
        if (field.slug) values[field.slug] = field.value;
      } else {
        field_values[field_id] = field;
      }
    });
  }

  return { field_values, values };
}

export function normalizeContact(row) {
  const data = unwrapSheetsData(row);
  const source = data?.contact || data;
  const canonical_contact_id =
    source?.canonical_contact_id || source?.contact_id || source?.id || null;
  if (!canonical_contact_id) return null;

  const { field_values, values } = normalizeFieldValues(
    source.fields || source.field_values,
  );

  const contact = {
    canonical_contact_id,
    version: source.version ?? null,
    field_values,
    values: {
      ...values,
      ...(source.values || {}),
    },
  };
  // CRM_Canonical_Contact_Aliases links a canonical contact to its legacy
  // record through source_crm_object_id. Omitted when the response leaves the
  // property out, so a partial mutation response merged over a cached contact
  // can't clobber a known alias. An explicit null is authoritative.
  if (source && "source_crm_object_id" in source) {
    contact.source_crm_object_id = source.source_crm_object_id ?? null;
  }
  return contact;
}

export function normalizeSheetRows(payload) {
  const data = unwrapSheetsData(payload) || {};
  const items = Array.isArray(data)
    ? data
    : data.rows || data.contacts || data.items || [];
  const contacts_by_id = {};
  const ids = [];

  items.forEach((item) => {
    const contact = normalizeContact(item);
    if (!contact || contacts_by_id[contact.canonical_contact_id]) return;
    contacts_by_id[contact.canonical_contact_id] = contact;
    ids.push(contact.canonical_contact_id);
  });

  const pagination = data.pagination || data.page || {};
  return {
    contacts_by_id,
    ids,
    cursor: pagination.cursor ?? data.cursor ?? null,
    next_cursor: pagination.next_cursor ?? data.next_cursor ?? null,
    total:
      pagination.total ??
      pagination.total_entries ??
      data.total ??
      data.matched_count ??
      ids.length,
  };
}

export function normalizeSheetSettings(payload) {
  const data = unwrapSheetsData(payload) || {};
  return data.settings || data.user_settings || data;
}

export function normalizeSheetColumns(payload) {
  const data = unwrapSheetsData(payload) || {};
  if (Array.isArray(data)) {
    return { columns: data, version: null };
  }
  return {
    columns: data.columns || data.user_columns || [],
    version: data.version ?? null,
  };
}

export function normalizeSheetCollection(payload, key) {
  const data = unwrapSheetsData(payload) || {};
  if (Array.isArray(data)) return data;
  return data[key] || data.items || [];
}

// The state a finished delivery reports. A batch that is still pending or
// applying, or that failed, has not delivered anything to announce.
export const COMPLETED_DELIVERY_STATE = "completed";

// A delivery the API left unlabelled is not assumed to have failed: a missing
// outcome is silence, not a negative answer.
export function deliveryHasLanded(delivery) {
  return !delivery?.state || delivery.state === COMPLETED_DELIVERY_STATE;
}

// Sheets only reports that a delivery landed and when. Round, batch, job and
// service ids stay out of the store so no component can put one on screen.
// The outcome is kept so the latest-round view can tell batches apart.
export function normalizeSheetDeliveries(payload) {
  return normalizeSheetCollection(payload, "deliveries")
    .map((delivery) => ({
      delivered_at: delivery?.delivered_at || delivery?.completed_at || null,
      state: delivery?.state || delivery?.status || null,
    }))
    .filter((delivery) => delivery.delivered_at)
    .sort(
      (left, right) =>
        new Date(right.delivered_at) - new Date(left.delivered_at)
    );
}

export function normalizeSheetJob(payload) {
  const data = unwrapSheetsData(payload) || {};
  const job = data.job || data;
  const job_id = job.job_id || job.id || null;
  if (!job_id) return null;

  return {
    job_id,
    sheet_id: job.sheet_id || null,
    type: job.type || job.job_type || null,
    state: job.state || job.status || null,
    progress: job.progress ?? null,
    counts: job.counts || {},
    error: job.error || null,
    result: job.result || null,
  };
}

export function isSheetsRequestCancelled(error) {
  return (
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError" ||
    error?.name === "AbortError"
  );
}

export function normalizeSheetsError(error) {
  const status = error?.response?.status || null;
  const response_data = error?.response?.data || {};
  const contract_error = response_data.error || response_data;
  let state = ERROR_STATE_BY_STATUS[status] || null;

  if (!state && status >= 500) state = "server_error";
  if (!state && !error?.response) state = "network_error";
  if (!state) state = "error";

  return {
    state,
    status,
    code: contract_error.code || null,
    message: contract_error.message || error?.message || "",
    request_id: contract_error.request_id || null,
    details: contract_error.details || {},
    retryable:
      state === "rate_limited" ||
      state === "server_error" ||
      state === "network_error",
  };
}
